import React, { useState, useEffect, useRef } from "react";
import { useBreakpoint } from "../hooks";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useCatConfig } from "../store/catStore";
import { Ic, fmt, disc, thumb, COLORS_MAP, printShippingLabel } from "../utils/helpers";
import { apiGetProduct, apiCreateReview, apiMarkHelpful, apiToggleWishlist, apiGetWishlist } from "../api";
import Gallery from "../components/ui/Gallery";
import { Check, Copy, Heart, Lock, RefreshCw, Share2, TrendingUp, Truck, Zap } from "lucide-react";
import { Stars, Spinner } from "../components/ui/Common";
import { useSettings } from "../store/contentStore";

/* ── PRODUCT DETAIL ──────────────────────────────────────────────────────────── */
const ProductDetailPage = ({ productId, setPage, toast, fromCat = "", fromSubCat = "" }) => {
  const {isMobile}=useBreakpoint();
  const st=useSettings();
  const {user}=useAuth();
  const {addToCart,cart}=useCart();
  const [data,setData]=useState(null);
  const [revs,setRevs]=useState([]);
  const [loading,setLoading]=useState(true);
  const [selSize,setSelSize]=useState("");
  const [selColor,setSelColor]=useState("");
  const [qty,setQty]=useState(1);
  const [tab,setTab]=useState("description");
  const [newRev,setNewRev]=useState({rating:5,comment:""});
  const [revMsg,setRevMsg]=useState("");
  const [wished,setWished]=useState(false);
  const [copied,setCopied]=useState(false);

  useEffect(()=>{
    setLoading(true);
    apiGetProduct(productId).then(r=>{setData(r?.data?.product||null);setRevs(Array.isArray(r?.data?.reviews)?r.data.reviews:[]); setSelColor(r.data.product.colors?.[0]||"");setSelSize(r.data.product.sizes?.[0]||"");}).catch(console.error).finally(()=>setLoading(false));
    if(user)apiGetWishlist().then(r=>setWished(r.data.wishlist.some(w=>(w._id||w)===productId))).catch(()=>{});
  },[productId,user]);

  const toggleWish=async()=>{
    if(!user){setPage("login");return;}
    const r=await apiToggleWishlist(productId);
    setWished(r.data.wishlist.includes(productId));
    toast(r.data.wishlist.includes(productId)?"Added to wishlist ♡":"Removed from wishlist");
  };

  const productUrl=()=>`${window.location.origin}/?product=${productId}`;

  const copyLink=()=>{
    navigator.clipboard.writeText(productUrl()).then(()=>{
      setCopied(true);
      setTimeout(()=>setCopied(false),2000);
    });
  };

  const shareWhatsApp=()=>{
    const msg=encodeURIComponent(`Check out "${data?.name}" on BanyanVision!\n${productUrl()}`);
    window.open(`https://wa.me/?text=${msg}`,"_blank");
  };

  const shareNative=()=>{
    if(navigator.share){
      navigator.share({title:data?.name,text:`Check out this product on BanyanVision!`,url:productUrl()}).catch(()=>{});
    }else{
      copyLink();
    }
  };

  const submitRev=async()=>{
    if(!user){setPage("login");return;}
    if(!newRev.comment.trim()){setRevMsg("Please write a comment.");return;}
    try{
      const r=await apiCreateReview({productId,...newRev});
      setRevs(p=>[r.data.review,...p]);
      setNewRev({rating:5,comment:""});
      setRevMsg("Review submitted! ★");
      setTimeout(()=>setRevMsg(""),3000);
    }catch(err){setRevMsg(err.response?.data?.message||"Error");}
  };

  const catCfg=useCatConfig();

  if(loading)return <div style={{background:"var(--ivory)",minHeight:"60vh"}}><Spinner/></div>;
  if(!data)return <div style={{textAlign:"center",padding:80,color:"var(--muted)"}}>Product not found</div>;
  const d=disc(data.originalPrice,data.price);
  const cfg=catCfg[data.category]||{grad:"linear-gradient(135deg,var(--rose),var(--saffron))",light:"var(--roseL)",icon:"👗"};
  const iStyle={background:"var(--ivory2)",border:"1.5px solid var(--border2)",color:"var(--text)",padding:"12px 14px",fontSize:13,borderRadius:12,outline:"none",width:"100%",fontWeight:500};

  const goCollection = () => {
    const cat = fromCat || data.category;
    const sub = fromSubCat || data.subCategory || "";
    setPage("shop:" + cat + (sub ? ":" + sub : ""));
  };

  return (
    <div style={{background:"var(--ivory)",minHeight:"100vh"}}>
      {/* ── Fixed floating back button (bottom-right) ── */}
      <button onClick={goCollection} style={{
        position:"fixed", bottom:isMobile?80:32, right:isMobile?16:32, zIndex:500,
        display:"flex", alignItems:"center", gap:8,
        background:"linear-gradient(135deg,var(--rose),var(--saffron))",
        color:"#fff", border:"none", borderRadius:50,
        padding:isMobile?"12px 18px":"13px 22px",
        fontSize:isMobile?12:13, fontWeight:700, cursor:"pointer",
        boxShadow:"0 6px 24px rgba(194,24,91,.4)",
        transition:"all .2s",
      }}
        onMouseEnter={e=>e.currentTarget.style.transform="translateY(-3px) scale(1.04)"}
        onMouseLeave={e=>e.currentTarget.style.transform="none"}
      >
        <span style={{fontSize:15}}>‹</span>
        {isMobile ? (fromSubCat || data.subCategory || "Collection") : `Back to ${fromSubCat || data.subCategory || data.category}`}
      </button>

      <div style={{maxWidth:1300,margin:"0 auto",padding:isMobile?"16px":"40px 80px"}}>
        <nav style={{display:"flex",gap:6,alignItems:"center",marginBottom:24,flexWrap:"wrap"}}>
          {/* Back to collection — top left */}
          <button onClick={goCollection} style={{
            display:"flex", alignItems:"center", gap:6,
            background:"#fff", border:"1.5px solid var(--border2)",
            borderRadius:10, padding:"7px 14px",
            fontSize:12, fontWeight:700, color:"var(--rose)", cursor:"pointer",
            transition:"all .2s", marginRight:8,
            boxShadow:"0 2px 8px rgba(194,24,91,.1)",
          }}
            onMouseEnter={e=>{e.currentTarget.style.background="var(--roseL)";e.currentTarget.style.borderColor="var(--rose)";}}
            onMouseLeave={e=>{e.currentTarget.style.background="#fff";e.currentTarget.style.borderColor="var(--border2)";}}
          >
            <span style={{fontSize:15,lineHeight:1}}>‹</span>
            {fromSubCat || data.subCategory
              ? <>{fromCat || data.category} <span style={{opacity:.6,fontWeight:500}}>›</span> {fromSubCat || data.subCategory}</>
              : (fromCat || data.category)
            }
          </button>

          <span style={{fontSize:12,color:"var(--muted)",fontWeight:600,display:"flex",alignItems:"center",gap:6}}>
            <span onClick={()=>setPage("home")} style={{cursor:"pointer",color:"var(--muted)"}}>Home</span>
            <span>›</span>
            <span onClick={()=>setPage("shop")} style={{cursor:"pointer",color:"var(--muted)"}}>Shop</span>
            <span>›</span>
            <span style={{color:"var(--text)",maxWidth:isMobile?160:320,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{data.name}</span>
          </span>
        </nav>

        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:isMobile?24:64,marginBottom:52}}>
          <Gallery images={data.images} name={data.name}/>

          <div>
            <div className="tag" style={{background:cfg.light,marginBottom:14,fontSize:11}}><span>{cfg.icon}</span><span style={{background:cfg.grad,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",fontWeight:800}}>{data.category}</span></div>
            <h1 style={{fontFamily:"var(--font-d)",fontSize:isMobile?24:36,fontWeight:700,color:"var(--dark)",marginBottom:12,lineHeight:1.2}}>{data.name}</h1>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
              <Stars rating={data.rating||0} size={15}/>
              <span style={{fontWeight:700,fontSize:14,color:"var(--text)"}}>{(data.rating||0).toFixed(1)}</span>
              <span style={{color:"var(--muted)",fontSize:13}}>({data.numReviews||0} reviews)</span>
              {data.trending&&<span className="tag" style={{background:"var(--saffronL)",color:"var(--saffron)"}}><span style={{display:"flex",alignItems:"center",gap:4}}><Ic icon={TrendingUp} size={11}/>Trending</span></span>}
            </div>

            <div style={{display:"flex",alignItems:"baseline",gap:14,marginBottom:20,paddingBottom:20,borderBottom:"1.5px solid var(--border)"}}>
              <span style={{fontFamily:"var(--font-d)",fontSize:isMobile?30:40,fontWeight:700,background:"linear-gradient(135deg,var(--rose),var(--saffron))",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>{fmt(data.price)}</span>
              {data.originalPrice&&<span style={{fontSize:18,color:"var(--muted)",textDecoration:"line-through"}}>{fmt(data.originalPrice)}</span>}
              {d>0&&<span className="tag" style={{background:"#F0FDF4",color:"#16A34A",fontWeight:800}}>Save {fmt(data.originalPrice-data.price)}</span>}
            </div>

            {/* Colours */}
            {data.colors?.length>0&&(
              <div style={{marginBottom:18}}>
                <div style={{fontSize:12,fontWeight:700,color:"var(--text2)",marginBottom:10}}>Colour: <span style={{color:"var(--rose)"}}>{selColor}</span></div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {data.colors.map(c=>(
                    <button key={c} onClick={()=>setSelColor(c)} style={{display:"flex",alignItems:"center",gap:7,padding:"7px 14px",borderRadius:10,border:`2px solid ${selColor===c?"var(--rose)":"var(--border2)"}`,background:selColor===c?"var(--roseL)":"#fff",cursor:"pointer",fontSize:12,fontWeight:600,color:selColor===c?"var(--rose)":"var(--text2)",transition:"all .2s"}}>
                      <div style={{width:13,height:13,borderRadius:"50%",background:COLORS_MAP[c]||"#999",border:"2px solid #fff",boxShadow:"0 1px 4px rgba(0,0,0,.15)"}}/>{c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Sizes */}
            {data.sizes?.length>0&&!["Free Size","One Size"].includes(data.sizes[0])&&(
              <div style={{marginBottom:20}}>
                <div style={{fontSize:12,fontWeight:700,color:"var(--text2)",marginBottom:10}}>Size: <span style={{color:"var(--rose)"}}>{selSize}</span></div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {data.sizes.map(s=>(
                    <button key={s} onClick={()=>setSelSize(s)} style={{width:46,height:44,borderRadius:10,border:`2px solid ${selSize===s?"var(--rose)":"var(--border2)"}`,background:selSize===s?"linear-gradient(135deg,var(--rose),var(--saffron))":"#fff",color:selSize===s?"#fff":"var(--text2)",cursor:"pointer",fontSize:13,fontWeight:700,transition:"all .2s"}}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div style={{display:"flex",gap:12,marginBottom:20,alignItems:"center"}}>
              <div style={{display:"flex",alignItems:"center",border:"1.5px solid var(--border2)",borderRadius:12,overflow:"hidden"}}>
                <button onClick={()=>setQty(Math.max(1,qty-1))} style={{width:42,height:44,background:"var(--ivory2)",border:"none",fontSize:20,fontWeight:700,color:"var(--rose)",cursor:"pointer"}}>−</button>
                <span style={{padding:"0 18px",fontWeight:800,fontSize:16,color:"var(--text)"}}>{qty}</span>
                <button onClick={()=>setQty(Math.min(data.stock,qty+1))} style={{width:42,height:44,background:"var(--ivory2)",border:"none",fontSize:20,fontWeight:700,color:"var(--rose)",cursor:"pointer"}}>+</button>
              </div>
              <span className="tag" style={{background:data.stock>0?(data.stock<=7?"#FEF3C7":"#F0FDF4"):"#FEF2F2",color:data.stock>0?(data.stock<=7?"#D97706":"#16A34A"):"#DC2626",fontWeight:800}}>
                {data.stock>0?(data.stock<=7?`Only ${data.stock} left!`:"In Stock"):"Out of Stock"}
              </span>
            </div>

            <div style={{display:"flex",gap:10,marginBottom:22,flexWrap:"wrap"}}>
              <button className="btn btn-outline" onClick={()=>{
                if(data.sizes?.length&&!["Free Size","One Size"].includes(data.sizes[0])&&!selSize){toast("Please select a size","error");return;}
                addToCart(data,qty,selSize,selColor);
                toast(`${data.name} added to bag!`);
              }} style={{flex:1,padding:"15px 0",fontSize:14,minWidth:130}}>
                Add to Bag
              </button>
              <button className="btn btn-rose" onClick={()=>{
                if(data.sizes?.length&&!["Free Size","One Size"].includes(data.sizes[0])&&!selSize){toast("Please select a size","error");return;}
                addToCart(data,qty,selSize,selColor);
                if(!user){setPage("login");return;}
                setPage("checkout");
              }} style={{flex:1.2,padding:"15px 0",fontSize:14,minWidth:140}}>
                ⚡ Buy Now
              </button>
              <button onClick={toggleWish} style={{width:52,background:wished?"var(--roseL)":"var(--ivory2)",border:`2px solid ${wished?"var(--rose)":"var(--border2)"}`,borderRadius:12,color:wished?"var(--rose)":"var(--muted)",fontSize:22,cursor:"pointer",transition:"all .2s"}}>
                {wished?<Ic icon={Heart} size={18} color="var(--rose)" style={{fill:"var(--rose)"}}/>:<Ic icon={Heart} size={18}/>}
              </button>
            </div>

            {/* Share row */}
            <div style={{display:"flex",gap:8,marginBottom:20,alignItems:"center"}}>
              <span style={{fontSize:11,fontWeight:700,color:"var(--muted)",letterSpacing:.5,textTransform:"uppercase",marginRight:4}}>Share:</span>
              <button onClick={copyLink} title="Copy link" style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:10,border:"1.5px solid var(--border2)",background:copied?"var(--roseL)":"#fff",color:copied?"var(--rose)":"var(--text2)",fontSize:12,fontWeight:600,cursor:"pointer",transition:"all .2s"}}>
                <Ic icon={Copy} size={13}/>{copied?"Copied!":"Copy Link"}
              </button>
              <button onClick={shareWhatsApp} title="Share on WhatsApp" style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:10,border:"1.5px solid #25D366",background:"#F0FFF4",color:"#128C7E",fontSize:12,fontWeight:700,cursor:"pointer",transition:"all .2s"}}
                onMouseEnter={e=>e.currentTarget.style.background="#25D366"}
                onMouseLeave={e=>e.currentTarget.style.background="#F0FFF4"}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </button>
              <button onClick={shareNative} title="Share" style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:10,border:"1.5px solid var(--border2)",background:"#fff",color:"var(--text2)",fontSize:12,fontWeight:600,cursor:"pointer",transition:"all .2s"}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor="var(--rose)";e.currentTarget.style.color="var(--rose)";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--border2)";e.currentTarget.style.color="var(--text2)";}}>
                <Ic icon={Share2} size={13}/>Share
              </button>
            </div>

            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {[["Fabric",data.fabric],["Occasion",data.occasion],["Care",data.care],["Category",data.category],data.subCategory?["Sub-Category",data.subCategory]:null].filter(Boolean).map(([k,v])=>(
                <div key={k} style={{padding:"12px 14px",background:"#fff",borderRadius:12,border:"1.5px solid var(--border)"}}>
                  <div style={{fontSize:9,fontWeight:700,letterSpacing:1.5,color:"var(--muted)",textTransform:"uppercase",marginBottom:3}}>{k}</div>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{v||"—"}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{display:"flex",borderBottom:"2px solid var(--border)",marginBottom:28,overflowX:"auto",gap:2}}>
          {["description","reviews","shipping"].map(t=>(
            <button key={t} onClick={()=>setTab(t)} style={{padding:"12px 20px",background:tab===t?"var(--roseL)":"transparent",border:"none",borderBottom:tab===t?"3px solid var(--rose)":"3px solid transparent",borderRadius:"10px 10px 0 0",color:tab===t?"var(--rose)":"var(--muted)",fontWeight:700,cursor:"pointer",fontSize:12,whiteSpace:"nowrap",marginBottom:-2,transition:"all .2s",textTransform:"capitalize"}}>
              {t}
            </button>
          ))}
        </div>

        {tab==="description"&&<p style={{color:"var(--text2)",lineHeight:2,fontSize:15,maxWidth:680,fontWeight:400}}>{data.description}</p>}

        {tab==="reviews"&&(
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"200px 1fr",gap:isMobile?24:44}}>
            <div style={{padding:24,borderRadius:"20px",background:"linear-gradient(135deg,var(--roseL),var(--saffronL))",textAlign:"center",height:"fit-content",border:"1.5px solid var(--border)"}}>
              <div style={{fontFamily:"var(--font-d)",fontSize:54,fontWeight:700,background:"linear-gradient(135deg,var(--rose),var(--saffron))",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",lineHeight:1}}>{(data.rating||0).toFixed(1)}</div>
              <Stars rating={data.rating||0} size={20}/>
              <div style={{color:"var(--muted)",fontSize:12,marginTop:6,fontWeight:600}}>{data.numReviews||0} reviews</div>
            </div>
            <div>
              {revs.length===0&&<p style={{color:"var(--muted)",fontStyle:"italic"}}>No reviews yet. Be the first!</p>}
              {revs.map(r=>(
                <div key={r._id} style={{borderBottom:"1.5px solid var(--border)",paddingBottom:18,marginBottom:18}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:8,flexWrap:"wrap",gap:6}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{width:36,height:36,background:"linear-gradient(135deg,var(--rose),var(--saffron))",borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:800,fontSize:14}}>{r.userName?.[0]}</div>
                      <div>
                        <div style={{fontWeight:700,fontSize:14,color:"var(--text)"}}>{r.userName} {r.verified&&<span style={{color:"#16A34A",fontSize:11}}>✓ Verified</span>}</div>
                        <div style={{fontSize:11,color:"var(--muted)"}}>{new Date(r.createdAt).toLocaleDateString("en-IN")}</div>
                      </div>
                    </div>
                    <Stars rating={r.rating} size={13}/>
                  </div>
                  <p style={{color:"var(--text2)",fontSize:13,lineHeight:1.8,paddingLeft:46}}>{r.comment}</p>
                  <button onClick={()=>apiMarkHelpful(r._id)} style={{marginLeft:46,marginTop:8,background:"var(--ivory2)",border:"1.5px solid var(--border)",padding:"4px 14px",borderRadius:8,fontSize:12,cursor:"pointer",fontWeight:600,color:"var(--text2)",transition:"all .2s"}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor="var(--rose)"}
                    onMouseLeave={e=>e.currentTarget.style.borderColor="var(--border)"}>
                    <Ic icon={Check} size={13}/> Helpful ({r.helpful})
                  </button>
                </div>
              ))}
              <div style={{padding:isMobile?18:26,borderRadius:"20px",background:"linear-gradient(135deg,var(--roseL),var(--saffronL))",border:"1.5px solid var(--border)"}}>
                <h4 style={{fontFamily:"var(--font-d)",color:"var(--dark)",margin:"0 0 14px",fontSize:20,fontWeight:700}}>Write a Review</h4>
                <Stars rating={newRev.rating} size={30} interactive onRate={r=>setNewRev(v=>({...v,rating:r}))}/>
                <textarea value={newRev.comment} onChange={e=>setNewRev(v=>({...v,comment:e.target.value}))} placeholder="Share your experience with this product…"
                  style={{width:"100%",minHeight:88,padding:"12px 14px",background:"#fff",border:"1.5px solid var(--border2)",fontSize:13,resize:"vertical",outline:"none",marginTop:12,color:"var(--text)",borderRadius:12,boxSizing:"border-box",fontFamily:"var(--font-b)"}}/>
                {revMsg&&<div style={{color:revMsg.includes("★")?"#16A34A":"#DC2626",fontSize:13,marginTop:6,fontWeight:700}}>{revMsg}</div>}
                <button className="btn btn-rose" onClick={submitRev} style={{marginTop:12,padding:"12px 28px",fontSize:13}}>Submit Review</button>
              </div>
            </div>
          </div>
        )}

        {tab==="shipping"&&(
          <div style={{maxWidth:600}}>
            {[[Truck,"Standard Delivery",st.standardDays||"3–5 business days",(st.freeShippingAbove===0?"Always Free":`Free above ₹${st.freeShippingAbove||2000} · ₹${st.shippingCharge||99} otherwise`)],[Zap,"Express Delivery",st.expressDays||"1–2 business days",`₹${st.expressCharge||199}`],...(st.returnsEnabled?[[RefreshCw,"Free Returns",`Within ${st.returnDays||7} days`,st.returnType||"Free pickup from door"]]:[]),[Lock,"Secure Payment","Razorpay","UPI · Cards · EMI · NetBanking"]].map(([DIcon,t,sub,val])=>(
              <div key={t} style={{display:"flex",gap:16,padding:"18px 0",borderBottom:"1.5px solid var(--border)",alignItems:"flex-start"}}>
                <div style={{width:46,height:46,background:"var(--roseL)",borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Ic icon={DIcon} size={22} color="var(--rose)"/></div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,color:"var(--text)",fontSize:14}}>{t}</div>
                  <div style={{color:"var(--muted)",fontSize:12,marginTop:2}}>{sub}</div>
                </div>
                <div style={{fontSize:13,fontWeight:700,color:"var(--rose)",textAlign:"right",flexShrink:0}}>{val}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;